/* Warmwell UI — one small script, no libraries.
   Product pack selection, gallery dots, sticky add-to-cart, cart drawer, menu sheet. */
(() => {
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency });
  const money = (cents) => fmt.format(cents / 100);
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Dialog sheets (menu + cart) ---------- */
  const openSheet = (d) => { if (d && !d.open) d.showModal(); };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-ww-open]');
    if (opener) { e.preventDefault(); openSheet(document.getElementById(opener.dataset.wwOpen)); return; }
    const closer = e.target.closest('[data-ww-close]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    if (e.target.matches('dialog.ww-sheet')) e.target.close(); /* click on the backdrop */
  });

  /* ---------- Cart ---------- */
  const cart = document.getElementById('ww-cart');

  const setCount = (n) => {
    $$('[data-ww-cart-count]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
    $$('[data-ww-cart-open]').forEach((el) => el.setAttribute('aria-label', `Cart, ${n} item${n === 1 ? '' : 's'}`));
  };

  const renderCart = async () => {
    if (!cart) return;
    const res = await fetch(`${root}?sections=ww-cart-drawer`, { headers: { Accept: 'application/json' } });
    const data = await res.json();
    const doc = new DOMParser().parseFromString(data['ww-cart-drawer'], 'text/html');
    const fresh = $('[data-ww-cart-inner]', doc);
    const current = $('[data-ww-cart-inner]', cart);
    if (fresh && current) {
      current.replaceWith(fresh);
      setCount(parseInt(fresh.dataset.count, 10) || 0);
    }
  };

  const busy = (on) => $('[data-ww-cart-inner]', cart)?.setAttribute('aria-busy', on);

  const post = (url, body) => fetch(`${root}${url}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });

  if (cart) {
    document.addEventListener('click', async (e) => {
      const opener = e.target.closest('[data-ww-cart-open]');
      if (opener) { e.preventDefault(); openSheet(cart); renderCart(); return; }

      const qtyBtn = e.target.closest('[data-ww-line]');
      if (qtyBtn && cart.contains(qtyBtn)) {
        busy(true);
        await post('cart/change.js', { line: +qtyBtn.dataset.wwLine, quantity: +qtyBtn.dataset.wwQty });
        await renderCart();
        return;
      }

      const swap = e.target.closest('[data-ww-swap-to]');
      if (swap) {
        busy(true);
        await post('cart/update.js', { updates: { [swap.dataset.wwSwapFrom]: 0, [swap.dataset.wwSwapTo]: 1 } });
        await renderCart();
      }
    });
  }

  /* ---------- Product form ---------- */
  const form = $('[data-ww-product-form]');
  if (form) {
    const atc = $('[data-ww-atc]', form);
    const atcText = $('[data-ww-atc-text]', form);
    const error = $('[data-ww-error]', form);
    const sticky = $('[data-ww-sticky]');
    const stickyTitle = $('[data-ww-sticky-title]');
    const stickyPrice = $('[data-ww-sticky-price]');
    const stickyBtn = sticky && $('button', sticky);

    const update = (input) => {
      const price = +input.dataset.price;
      atcText.textContent = `${atc.dataset.label} · ${money(price)}`;
      atc.disabled = input.disabled;
      if (stickyTitle) stickyTitle.textContent = input.dataset.title;
      if (stickyPrice) stickyPrice.textContent = money(price);
      const url = new URL(location.href);
      url.searchParams.set('variant', input.value);
      history.replaceState(history.state, '', url);
    };

    form.addEventListener('change', (e) => { if (e.target.name === 'id') update(e.target); });

    form.addEventListener('submit', async (e) => {
      if (!cart) return; /* no drawer: let the form post to /cart/add */
      e.preventDefault();
      error.hidden = true;
      atc.setAttribute('aria-busy', 'true');
      if (stickyBtn) stickyBtn.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${root}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
        await renderCart();
        openSheet(cart);
      } catch (err) {
        error.textContent = err.message;
        error.hidden = false;
      } finally {
        atc.removeAttribute('aria-busy');
        if (stickyBtn) stickyBtn.removeAttribute('aria-busy');
      }
    });

    /* Sticky bar: shown once the main button has scrolled out of view above. */
    if (sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        if (stickyBtn) stickyBtn.tabIndex = show ? 0 : -1;
      }).observe(atc);
    }
  }

  /* ---------- Gallery ---------- */
  $$('[data-ww-gallery]').forEach((g) => {
    const track = $('[data-ww-track]', g);
    const slides = [...track.children];
    const buttons = $$('[data-ww-go]', g);
    if (slides.length < 2) return;
    buttons.forEach((b) => b.addEventListener('click', () => {
      track.scrollTo({ left: slides[+b.dataset.wwGo].offsetLeft, behavior: reduced.matches ? 'auto' : 'smooth' });
    }));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const i = String(slides.indexOf(en.target));
        buttons.forEach((b) => b.setAttribute('aria-current', String(b.dataset.wwGo === i)));
      });
    }, { root: track, threshold: 0.6 });
    slides.forEach((s) => io.observe(s));
  });
})();
